import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useConfirmedMutation } from '../../lib/confirm';
import { Card, Typography, Button, Tag, Select, Input, InputNumber, DatePicker, Modal, Form, Popconfirm, Switch, Space, EmptyState, message, Tooltip } from '../../ui';
import { Plus, Pencil, Trash2, Info } from 'lucide-react';
import dayjs, { Dayjs } from 'dayjs';
import { request } from '../../api/client';
import { usePermission } from '../../store/auth';
import { TwDataTable as DataTable, type DataTableColumn } from '../../components/TwDataTable';
import { formatIstDate } from '../../lib/time';
import { inr2 } from '../../lib/format';

const BASIS_OPTIONS = [
  { value: 'PER_KM', label: 'Per kilometre (own vehicle)' },
  { value: 'ACTUAL', label: 'At actuals (ticketed / billed)' },
];

/**
 * Expense policy master — the reimbursement rates and daily-allowance slabs, held as data so they
 * can be changed without a release.
 *
 * Rates are EFFECTIVE-DATED: the page shows what applies on a chosen day (today by default), and a
 * change is recorded as a new row taking effect from a date you pick. Past claims keep the rate that
 * was in force when they happened.
 */
export function ExpensePolicyPage() {
  const qc = useQueryClient();
  const canEdit = usePermission('EXPENSE.POLICY.EDIT');
  const [onDate, setOnDate] = useState<Dayjs>(dayjs());
  const [rateModal, setRateModal] = useState<{ mode: 'create' | 'edit'; row?: any } | null>(null);
  const [slabModal, setSlabModal] = useState<{ mode: 'create' | 'edit'; row?: any } | null>(null);

  const params = useMemo(() => ({ on: onDate.format('YYYY-MM-DD') }), [onDate]);
  const { data, isLoading } = useQuery({
    queryKey: ['expense-policy', params],
    queryFn: () => request<any>({ url: '/expense-policy', params }),
  });

  const refresh = () => qc.invalidateQueries({ queryKey: ['expense-policy'] });
  const del = useMutation({
    mutationFn: ({ kind, id }: { kind: 'travel-rates' | 'da-slabs'; id: string }) =>
      request({ url: `/expense-policy/${kind}/${id}`, method: 'DELETE' }),
    onSuccess: () => { message.success('Removed'); refresh(); },
    onError: (e: any) => message.error(e?.response?.data?.error?.message ?? 'Failed'),
  });

  const rateColumns: DataTableColumn<any>[] = [
    {
      key: 'label', title: 'Travel mode', alwaysVisible: true, width: 200,
      render: (_: any, r: any) => (
        <div>
          <div className="font-medium text-content">{r.label}</div>
          <div className="text-2xs uppercase tracking-wide text-subtle">{r.code}</div>
        </div>
      ),
    },
    {
      key: 'basis', title: 'Reimbursed', width: 220,
      render: (_: any, r: any) => (r.basis === 'PER_KM'
        ? <Tag tone="green">{inr2(r.ratePerKm)} / km</Tag>
        : <Tag tone="neutral">At actuals</Tag>),
    },
    {
      key: 'proof', title: 'Proof', width: 220,
      render: (_: any, r: any) => (r.proofRequired
        ? <span className="text-content">{r.proofLabel || 'Required'}</span>
        : <span className="text-muted">Not required</span>),
    },
    { key: 'effectiveFrom', title: 'In force since', dataIndex: 'effectiveFrom', width: 140, render: (v: string) => formatIstDate(v) },
    ...(canEdit ? [{
      key: 'actions', title: '', width: 90, alwaysVisible: true,
      render: (_: any, r: any) => (
        <Space wrap size={4}>
          <Tooltip label="Correct this rate"><Button variant="text" size="sm" aria-label="Edit" icon={<Pencil size={16} />} onClick={() => setRateModal({ mode: 'edit', row: r })} /></Tooltip>
          <Popconfirm title="Remove this travel mode?" description="Claims already priced by it keep their amounts." onConfirm={() => del.mutate({ kind: 'travel-rates', id: r.id })}>
            <Button variant="text" size="sm" aria-label="Remove" icon={<Trash2 size={16} />} className="text-muted hover:bg-danger-500/10 hover:text-danger-600" />
          </Popconfirm>
        </Space>
      ),
    } as DataTableColumn<any>] : []),
  ];

  const slabColumns: DataTableColumn<any>[] = [
    { key: 'minKm', title: 'When the day covers', dataIndex: 'minKm', alwaysVisible: true, width: 220, render: (v: number) => <span className="font-medium text-content">{v} km or more</span> },
    { key: 'amount', title: 'Allowance', dataIndex: 'amount', width: 180, render: (v: number) => <Tag tone="green">{inr2(v)} / day</Tag> },
    { key: 'effectiveFrom', title: 'In force since', dataIndex: 'effectiveFrom', width: 140, render: (v: string) => formatIstDate(v) },
    ...(canEdit ? [{
      key: 'actions', title: '', width: 90, alwaysVisible: true,
      render: (_: any, r: any) => (
        <Space wrap size={4}>
          <Tooltip label="Correct this slab"><Button variant="text" size="sm" aria-label="Edit" icon={<Pencil size={16} />} onClick={() => setSlabModal({ mode: 'edit', row: r })} /></Tooltip>
          <Popconfirm title="Remove this slab?" onConfirm={() => del.mutate({ kind: 'da-slabs', id: r.id })}>
            <Button variant="text" size="sm" aria-label="Remove" icon={<Trash2 size={16} />} className="text-muted hover:bg-danger-500/10 hover:text-danger-600" />
          </Popconfirm>
        </Space>
      ),
    } as DataTableColumn<any>] : []),
  ];

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <Typography.Title level={4} style={{ margin: 0 }}>Expense policy</Typography.Title>
      </div>
      <Typography.Paragraph type="secondary" style={{ marginTop: 0 }}>
        What the company pays back for field travel. Rates are dated — a change applies from the day you
        choose and never re-prices claims that were already made at the old rate.
      </Typography.Paragraph>

      <div className="mb-4 flex flex-wrap items-center gap-2 rounded-card border border-line bg-surface-2 px-3 py-2.5 text-sm">
        <Info size={16} className="shrink-0 text-muted" />
        <span className="text-muted">Showing the policy in force on</span>
        <DatePicker value={onDate} onChange={(d) => d && setOnDate(d)} allowClear={false} format="DD MMM YYYY" />
        {!onDate.isSame(dayjs(), 'day') && (
          <Button variant="text" size="sm" onClick={() => setOnDate(dayjs())}>Back to today</Button>
        )}
      </div>

      <Card>
        <div className="mb-3 flex flex-wrap items-center gap-3">
          <div>
            <h3 className="type-body-strong text-content">Travel rates</h3>
            <p className="mt-0.5 text-xs text-muted">Own vehicle pays by the kilometre; ticketed travel is reimbursed as spent.</p>
          </div>
          {canEdit && (
            <Button variant="primary" size="sm" className="ml-auto" icon={<Plus size={16} />} onClick={() => setRateModal({ mode: 'create' })}>
              Add travel mode
            </Button>
          )}
        </div>
        <DataTable
          storageKey="expense-policy-rates"
          rowKey="id"
          loading={isLoading}
          dataSource={data?.travelRates ?? []}
          columns={rateColumns}
          pagination={false}
          showToolbar={false}
          emptyText={<EmptyState title="No travel rates set" />}
        />
      </Card>

      <div className="h-4" />

      <Card>
        <div className="mb-3 flex flex-wrap items-center gap-3">
          <div>
            <h3 className="type-body-strong text-content">Daily allowance</h3>
            <p className="mt-0.5 text-xs text-muted">Paid per field day, decided by the distance that day covers. The highest matching slab wins.</p>
          </div>
          {canEdit && (
            <Button variant="primary" size="sm" className="ml-auto" icon={<Plus size={16} />} onClick={() => setSlabModal({ mode: 'create' })}>
              Add slab
            </Button>
          )}
        </div>
        <DataTable
          storageKey="expense-policy-slabs"
          rowKey="id"
          loading={isLoading}
          dataSource={data?.daSlabs ?? []}
          columns={slabColumns}
          pagination={false}
          showToolbar={false}
          emptyText={<EmptyState title="No allowance slabs set" />}
        />
      </Card>

      {rateModal && <TravelRateModal state={rateModal} onClose={() => setRateModal(null)} onSaved={refresh} />}
      {slabModal && <DaSlabModal state={slabModal} onClose={() => setSlabModal(null)} onSaved={refresh} />}
    </div>
  );
}

function TravelRateModal({ state, onClose, onSaved }: { state: { mode: 'create' | 'edit'; row?: any }; onClose: () => void; onSaved: () => void }) {
  const [form] = Form.useForm();
  const editing = state.mode === 'edit';
  const basis = Form.useWatch('basis', form) ?? state.row?.basis ?? 'PER_KM';

  const save = useConfirmedMutation({ title: "Save this travel rate?", content: "New expense claims are priced by it. Claims already priced keep their amounts.", okText: "Yes, save" }, {
    mutationFn: (vals: any) => request({
      url: editing ? `/expense-policy/travel-rates/${state.row.id}` : '/expense-policy/travel-rates',
      method: editing ? 'PATCH' : 'POST',
      data: {
        code: vals.code, label: vals.label, basis: vals.basis,
        ratePerKm: vals.basis === 'PER_KM' ? Number(vals.ratePerKm) : null,
        proofRequired: vals.proofRequired !== false,
        proofLabel: vals.proofLabel || null,
        effectiveFrom: vals.effectiveFrom.format('YYYY-MM-DD'),
      },
    }),
    onSuccess: () => { message.success(editing ? 'Rate updated' : 'Rate added'); onSaved(); onClose(); },
    onError: (e: any) => message.error(e?.response?.data?.error?.message ?? 'Failed to save'),
  });

  return (
    <Modal
      open onOpenChange={(o) => { if (!o) onClose(); }}
      title={editing ? `Edit ${state.row.label}` : 'Add a travel mode'}
      footer={<><Button variant="default" onClick={onClose}>Cancel</Button><Button variant="primary" loading={save.isPending} onClick={() => form.submit()}>Save</Button></>}
    >
      <Form
        form={form} layout="vertical" onFinish={(v) => save.mutate(v)}
        initialValues={editing
          ? { ...state.row, effectiveFrom: dayjs(state.row.effectiveFrom) }
          : { basis: 'PER_KM', proofRequired: true, effectiveFrom: dayjs() }}
      >
        <Form.Item name="code" label="Code" rules={[{ required: true, message: 'A short code, e.g. CAR' }]} extra="Uppercase identifier — CAR, BIKE, TRAIN…">
          <Input placeholder="CAR" disabled={editing} />
        </Form.Item>
        <Form.Item name="label" label="Name" rules={[{ required: true, message: 'What reps see, e.g. Car' }]}>
          <Input placeholder="Car" />
        </Form.Item>
        <Form.Item name="basis" label="How it is reimbursed" rules={[{ required: true }]}>
          <Select options={BASIS_OPTIONS} />
        </Form.Item>
        {basis === 'PER_KM' && (
          <Form.Item name="ratePerKm" label="Rate per kilometre (₹)" rules={[{ required: true, message: 'Enter the per-km rate' }]}>
            <InputNumber min={0.01} prefix="₹" className="w-full" placeholder="9.00" />
          </Form.Item>
        )}
        <Form.Item name="proofRequired" label="Proof required" valuePropName="checked">
          <Switch />
        </Form.Item>
        <Form.Item name="proofLabel" label="What counts as proof" extra="Shown to the rep when they attach it — e.g. Ticket, Odometer photo.">
          <Input placeholder="Ticket" />
        </Form.Item>
        <Form.Item name="effectiveFrom" label="In force from" rules={[{ required: true, message: 'Pick the first day this applies' }]}
          extra="Claims for days before this keep whatever rate applied then.">
          <DatePicker className="w-full" format="DD MMM YYYY" />
        </Form.Item>
      </Form>
    </Modal>
  );
}

function DaSlabModal({ state, onClose, onSaved }: { state: { mode: 'create' | 'edit'; row?: any }; onClose: () => void; onSaved: () => void }) {
  const [form] = Form.useForm();
  const editing = state.mode === 'edit';

  const save = useConfirmedMutation({ title: "Save this DA slab?", content: "New day claims are priced by it. Claims already priced keep their amounts.", okText: "Yes, save" }, {
    mutationFn: (vals: any) => request({
      url: editing ? `/expense-policy/da-slabs/${state.row.id}` : '/expense-policy/da-slabs',
      method: editing ? 'PATCH' : 'POST',
      data: { minKm: Number(vals.minKm), amount: Number(vals.amount), effectiveFrom: vals.effectiveFrom.format('YYYY-MM-DD') },
    }),
    onSuccess: () => { message.success(editing ? 'Slab updated' : 'Slab added'); onSaved(); onClose(); },
    onError: (e: any) => message.error(e?.response?.data?.error?.message ?? 'Failed to save'),
  });

  return (
    <Modal
      open onOpenChange={(o) => { if (!o) onClose(); }}
      title={editing ? 'Edit allowance slab' : 'Add an allowance slab'}
      footer={<><Button variant="default" onClick={onClose}>Cancel</Button><Button variant="primary" loading={save.isPending} onClick={() => form.submit()}>Save</Button></>}
    >
      <Form
        form={form} layout="vertical" onFinish={(v) => save.mutate(v)}
        initialValues={editing ? { ...state.row, effectiveFrom: dayjs(state.row.effectiveFrom) } : { minKm: 80, effectiveFrom: dayjs() }}
      >
        <Form.Item name="minKm" label="Pay when the day covers at least (km)" rules={[{ required: true, message: 'Enter the distance threshold' }]}
          extra="A slab at 0 km pays on every field day.">
          <InputNumber min={0} className="w-full" placeholder="80" />
        </Form.Item>
        <Form.Item name="amount" label="Allowance per day (₹)" rules={[{ required: true, message: 'Enter the amount' }]}>
          <InputNumber min={0} prefix="₹" className="w-full" placeholder="150" />
        </Form.Item>
        <Form.Item name="effectiveFrom" label="In force from" rules={[{ required: true, message: 'Pick the first day this applies' }]}>
          <DatePicker className="w-full" format="DD MMM YYYY" />
        </Form.Item>
      </Form>
    </Modal>
  );
}
